import { Instagram, Linkedin, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageProvider";

export default function Footer() {
    const { t, lang } = useT();
    const base = lang === "hi" ? "/hi" : "";

    const links = [
        [`${base}/#story`, t("nav.mission"), "mission"],
        [`${base}/#how`, t("nav.how"), "how"],
        [`${base}/#stats`, t("nav.impact"), "impact"],
        [`${base}/#founder`, t("nav.founder"), "founder"],
        [`${base}/#waitlist`, t("nav.join"), "join"],
    ];

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-primary text-primary-foreground"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-24 pb-10">
                <div className="grid md:grid-cols-12 gap-12 mb-16">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-accent font-semibold mb-4">
                            Ain't No Waste.
                        </div>
                        <p className="font-display text-3xl md:text-4xl leading-tight tracking-tight max-w-md">
                            {t("footer.tagline")}
                        </p>
                    </div>
                    <nav aria-label="Footer" className="md:col-span-3 md:col-start-7">
                        <div className="text-xs uppercase tracking-[0.25em] text-primary-foreground/50 font-semibold mb-4">
                            {t("footer.explore")}
                        </div>
                        <ul className="space-y-3">
                            {links.map(([href, label, key]) => (
                                <li key={key}>
                                    <a
                                        href={href}
                                        data-testid={`footer-link-${key}`}
                                        className="text-primary-foreground/85 hover:text-accent transition-colors"
                                    >
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <address className="md:col-span-3 not-italic">
                        <div className="text-xs uppercase tracking-[0.25em] text-primary-foreground/50 font-semibold mb-4">
                            {t("footer.contact")}
                        </div>
                        <a
                            href="mailto:teamchigle@gmail.com"
                            data-testid="footer-email"
                            className="block text-primary-foreground hover:text-accent transition-colors mb-2"
                        >
                            teamchigle@gmail.com
                        </a>
                        <div className="text-primary-foreground/60 text-sm mb-6">
                            {t("footer.location")}
                        </div>
                        <div className="flex gap-3">
                            <a
                                href="mailto:teamchigle@gmail.com"
                                aria-label="Email CHIGLE team"
                                data-testid="footer-social-mail"
                                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-primary-foreground/30 hover:border-accent hover:text-accent transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <Mail size={16} />
                            </a>
                            <a
                                href="https://www.linkedin.com/in/ridhi-tiwari-968b1127a"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn (opens in new tab)"
                                data-testid="footer-social-linkedin"
                                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-primary-foreground/30 hover:border-accent hover:text-accent transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <Linkedin size={16} />
                            </a>
                            <a
                                href="https://www.instagram.com/chigle.co/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram (opens in new tab)"
                                data-testid="footer-social-instagram"
                                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-primary-foreground/30 hover:border-accent hover:text-accent transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <Instagram size={16} />
                            </a>
                        </div>
                    </address>
                </div>

                <div
                    data-testid="footer-logotype"
                    aria-hidden="true"
                    className="font-display tracking-tighter leading-none select-none uppercase"
                    style={{ fontSize: "clamp(5rem, 22vw, 22rem)" }}
                >
                    CHIGLE<span className="text-accent">.</span>
                </div>

                <div className="mt-10 pt-8 border-t border-primary-foreground/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-primary-foreground/55">
                    <div data-testid="footer-copyright">
                        © {new Date().getFullYear()} CHIGLE · chigle.co · {t("footer.copy.suffix")}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <Link
                            to={`${base}/privacy`}
                            data-testid="footer-privacy"
                            className="hover:text-accent transition-colors"
                        >
                            {t("footer.privacy")}
                        </Link>
                        <span className="opacity-30" aria-hidden="true">·</span>
                        <Link
                            to={`${base}/terms`}
                            data-testid="footer-terms"
                            className="hover:text-accent transition-colors"
                        >
                            {t("footer.terms")}
                        </Link>
                        <span className="opacity-30" aria-hidden="true">·</span>
                        <span>{t("footer.builtFor")}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
